/**
 * @fileoverview React hook for the authenticated team member's profile.
 *
 * Wraps the `getTeamProfile`, `updateTeamProfile`, and `getActivityLogs`
 * Firebase Functions with TanStack Query. Provides:
 *
 * - The team member's profile (name, email, phone, avatar, etc.)
 * - Their assigned projects (summary data for rendering)
 * - Their 15 most recent activity log entries
 * - A mutation for updating editable profile fields
 *
 * The hook derives the target user from the current Firebase auth
 * session server-side, so no email/uid arguments are required.
 *
 * @requires @tanstack/react-query
 * @requires firebase/functions
 * @requires ../firebase
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

/**
 * Call the `getTeamProfile` Firebase Function.
 * @returns {Promise<{profile: Object|null, assignedProjects: Array}>}
 */
const fetchTeamProfile = async () => {
  const getTeamProfile = httpsCallable(functions, "getTeamProfile");
  const result = await getTeamProfile();

  if (!result.data || !result.data.success) {
    throw new Error(result.data?.error || "Failed to fetch team profile");
  }

  return result.data.data;
};

/**
 * Call the `updateTeamProfile` Firebase Function.
 * @param {Object} fields - Fields to update
 * @param {string} [fields.displayName]
 * @param {string} [fields.phone]
 * @param {string} [fields.avatarUrl]
 * @returns {Promise<Object>}
 */
const updateTeamProfileFn = async (fields) => {
  const updateTeamProfile = httpsCallable(functions, "updateTeamProfile");
  const result = await updateTeamProfile(fields || {});

  if (!result.data || !result.data.success) {
    throw new Error(result.data?.error || "Failed to update team profile");
  }

  return result.data;
};

/**
 * Call the `getActivityLogs` Firebase Function.
 * @returns {Promise<Array>} Array of activity log entries
 */
const fetchActivityLogs = async () => {
  const getActivityLogs = httpsCallable(functions, "getActivityLogs");
  const result = await getActivityLogs();

  if (!result.data || !result.data.success) {
    throw new Error(result.data?.error || "Failed to fetch activity logs");
  }

  return result.data.data?.logs || [];
};

/**
 * Hook for the authenticated team member's profile, assigned projects,
 * recent activity logs, and profile update mutation.
 *
 * @returns {Object} Hook state and actions
 * @property {Object|null} profile - Team member profile (or null if
 *   no matching Team record exists for the signed-in email)
 * @property {Array} assignedProjects - Summary data for the team
 *   member's assigned projects
 * @property {Array} activityLogs - Up to 15 most recent activity
 *   log entries for the signed-in user
 * @property {boolean} isLoading - True while the profile or logs
 *   queries are loading for the first time
 * @property {boolean} isError - True if either query errored
 * @property {Function} updateProfile - Mutation function accepting
 *   `{ displayName?, phone?, avatarUrl? }`
 * @property {boolean} isUpdating - True while the update mutation
 *   is in flight
 *
 * @example
 * const { profile, assignedProjects, activityLogs, updateProfile } =
 *   useTeamProfile();
 *
 * updateProfile({ displayName: "Jane D.", phone: "555-1212" });
 */
export function useTeamProfile() {
  const queryClient = useQueryClient();

  const profileQuery = useQuery({
    queryKey: ["teamProfile"],
    queryFn: fetchTeamProfile,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const activityLogsQuery = useQuery({
    queryKey: ["activityLogs"],
    queryFn: fetchActivityLogs,
    staleTime: 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const updateMutation = useMutation({
    mutationFn: updateTeamProfileFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teamProfile"] });
    },
    onError: (error) => {
      console.error("Error updating team profile:", error);
    },
  });

  return {
    profile: profileQuery.data?.profile ?? null,
    assignedProjects: profileQuery.data?.assignedProjects ?? [],
    activityLogs: activityLogsQuery.data ?? [],
    isLoading: profileQuery.isLoading || activityLogsQuery.isLoading,
    isError: profileQuery.isError || activityLogsQuery.isError,
    error: profileQuery.error || activityLogsQuery.error,
    updateProfile: updateMutation.mutate,
    updateProfileAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
    refetch: profileQuery.refetch,
  };
}

export default useTeamProfile;
